import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { EmployeeService } from '../../services/employee.service';
import { OffboardingService } from '../../services/offboarding.service';
import { OffboardingSignalRService } from '../../services/offboarding-signalr.service';
import { Employee } from '../../models/employee.model';
import { OffboardingRecord, OffboardingTask, CreateOffboardingRecord } from '../../models/offboarding.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-offboarding-collection',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableModule,
    Button,
    Select,
    Dialog,
    InputText,
    Tooltip,
    Tag,
    ConfirmDialog,
    Checkbox
  ],
  providers: [ConfirmationService],
  templateUrl: './offboarding-collection.component.html',
  styleUrls: ['./offboarding-collection.component.scss']
})
export class OffboardingCollectionComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private employeeService = inject(EmployeeService);
  private offboardingService = inject(OffboardingService);
  private signalRService = inject(OffboardingSignalRService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  public pageHeaderService = inject(PageHeaderSearchActionService);

  records: OffboardingRecord[] = [];
  filteredRecords: OffboardingRecord[] = [];
  loading = false;
  searchText = '';
  selectedStatus: string | null = 'InProgress';

  rows = 15;
  first = 0;
  totalRecords = 0;
  rowsPerPageOptions = [15, 30, 50, 100];

  statusOptions = [
    { label: 'All', value: null },
    { label: 'In Progress', value: 'InProgress' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  // Offboarding Form Dialog
  displayFormDialog = false;
  formLoading = false;
  formSubmitted = false;
  formEditMode = false;
  formEditRecordId = '';
  formAttachmentUrl: string | null = null;
  formAttachmentName: string | null = null;
  formAttachmentUploading = false;
  employees: Employee[] = [];
  selectedEmployee: Employee | null = null;
  resignDate: Date = new Date();
  resignDateStr: string = new Date().toISOString().substring(0, 10);
  selectedReason: string = 'Personal';
  reasons = [
    { label: 'Personal', value: 'Personal' },
    { label: 'Career Growth', value: 'Career Growth' },
    { label: 'Relocation', value: 'Relocation' },
    { label: 'Other', value: 'Other' }
  ];

  // View/Edit details dialog
  displayViewDialog = false;
  viewingRecord: OffboardingRecord | null = null;
  isEditingView = false;
  editResignDate: Date = new Date();
  editReason = '';

  // Task dialog
  displayTaskDialog = false;
  selectedRecord: OffboardingRecord | null = null;
  dialogTasks: OffboardingTask[] = [];
  savingTasks = false;
  dialogDepartment: string | null = null;

  // Q&A dialog
  displayQADialog = false;
  qaAnswers: { id: string; question: string; answer: string }[] = [];
  savingQA = false;

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by employee name...');
    this.pageHeaderService.setPrimaryButtonLabel('New Offboarding');
    this.pageHeaderService.setCanShowPrimaryAction(true);
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);

    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadRecords();

    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => {
        this.searchText = v ?? '';
        this.applyFilters();
      });

    this.pageHeaderService.exportClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportToExcel());

    this.pageHeaderService.primaryActionClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showFormDialog());

    // Auto-open form if navigated with ?openForm=true
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params['openForm'] === 'true') {
        this.showFormDialog();
      }
    });

    // Real-time updates via SignalR
    this.signalRService.start();

    this.signalRService.recordCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRecords());

    this.signalRService.tasksUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRecords());

    this.signalRService.statusChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRecords());

    this.signalRService.questionnaireSubmitted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.messageService.add({
          severity: 'info',
          summary: 'Questionnaire Submitted',
          detail: `${data.employeeName} has submitted their exit questionnaire`
        });
        this.loadRecords();
      });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadRecords(): void {
    this.loading = true;
    const status = this.selectedStatus ?? undefined;

    this.offboardingService.getAllRecords(status)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load offboarding records' });
          this.loading = false;
          return of([] as OffboardingRecord[]);
        })
      )
      .subscribe({
        next: (data) => {
          this.records = data || [];
          this.applyFilters();
          this.loading = false;
        }
      });
  }

  applyFilters(): void {
    let filtered = [...this.records];

    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(r =>
        r.employeeName?.toLowerCase().includes(searchLower) ||
        r.companyName?.toLowerCase().includes(searchLower)
      );
    }

    if (this.selectedStatus !== null) {
      filtered = filtered.filter(r => r.status === this.selectedStatus);
    }

    this.filteredRecords = filtered;
    this.totalRecords = this.filteredRecords.length;
    this.first = 0;
  }

  onStatusFilterChange(): void {
    this.loadRecords();
  }

  clearFilters(): void {
    this.selectedStatus = null;
    this.loadRecords();
  }

  hasActiveFilters(): boolean {
    return this.selectedStatus !== null;
  }

  get showPaginator(): boolean {
    return this.totalRecords >= 15;
  }

  // ===== Form Dialog =====

  showFormDialog(): void {
    this.formEditMode = false;
    this.formEditRecordId = '';
    this.formSubmitted = false;
    this.selectedEmployee = null;
    this.resignDateStr = new Date().toISOString().substring(0, 10);
    this.selectedReason = 'Personal';
    this.formAttachmentUrl = null;
    this.formAttachmentName = null;
    this.formAttachmentUploading = false;
    this.displayFormDialog = true;
    this.loadEmployees();
  }

  hideFormDialog(): void {
    this.displayFormDialog = false;
    this.formEditMode = false;
    this.formEditRecordId = '';
    this.formAttachmentUrl = null;
    this.formAttachmentName = null;
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (file.size > 10 * 1024 * 1024) { input.value = ''; return; }
    this.formAttachmentUploading = true;
    this.offboardingService.uploadAttachment(file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => { this.formAttachmentUrl = res.url; this.formAttachmentName = res.fileName; this.formAttachmentUploading = false; },
        error: () => { input.value = ''; this.formAttachmentUploading = false; }
      });
  }

  removeAttachment(): void {
    this.formAttachmentUrl = null;
    this.formAttachmentName = null;
  }

  private loadEmployees(): void {
    this.employeeService.getAllEmployees()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => { this.employees = data.filter(e => e.isActive); },
        error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load employees' }); }
      });
  }

  submitOffboarding(): void {
    // Re-entrancy guard: the request takes a few seconds (it also sends the team notification emails),
    // and without this a second click fires a second POST that the server rejects with
    // "An active offboarding record already exists for this employee."
    if (this.formLoading) return;

    this.formSubmitted = true;
    if (!this.selectedEmployee) return;

    this.formLoading = true;
    const record: CreateOffboardingRecord = {
      employeeId: this.selectedEmployee.id,
      resignDate: this.resignDateStr,
      reason: this.selectedReason || 'Personal',
      attachmentUrl: this.formAttachmentUrl || undefined
    };

    const obs$ = this.formEditMode
      ? this.offboardingService.updateRecord(this.formEditRecordId, record)
      : this.offboardingService.createRecord(record);

    obs$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.formLoading = false;
          this.hideFormDialog();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: this.formEditMode ? 'Record updated' : 'Offboarding record created' });
          // Optimistic UI update — add/replace the record in local list immediately
          if (this.formEditMode) {
            const idx = this.records.findIndex(r => r.id === this.formEditRecordId);
            if (idx >= 0) this.records[idx] = created;
          } else {
            this.records.unshift(created);
          }
          this.applyFilters();
        },
        error: (err) => {
          this.formLoading = false;
          const detail = err.error?.details || err.error?.message || err.error?.title || 'Failed to save offboarding record';
          this.messageService.add({ severity: 'error', summary: 'Error', detail });
        }
      });
  }

  submitAndPlan(): void {
    if (this.formLoading) return;

    this.formSubmitted = true;
    if (!this.selectedEmployee) return;

    this.formLoading = true;
    const record: CreateOffboardingRecord = {
      employeeId: this.selectedEmployee.id,
      resignDate: this.resignDateStr,
      reason: this.selectedReason || 'Personal',
      attachmentUrl: this.formAttachmentUrl || undefined
    };

    this.offboardingService.createRecord(record)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Offboarding record created' });
          this.formLoading = false;
          this.hideFormDialog();
          // Optimistic UI update
          this.records.unshift(created);
          this.applyFilters();
          // Auto-open the task overview for planning
          this.viewRecord(created);
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create offboarding record' });
          this.formLoading = false;
        }
      });
  }

  // ===== Table Actions =====

  exportToExcel(): void {
    if (!this.filteredRecords.length) { this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No records to export' }); return; }
    const data = this.filteredRecords.map(r => ({
      'Employee Name': this.getEmployeeDisplay(r), 'Resign Date': r.resignDate, 'Reason': r.reason,
      'Status': r.status, 'P&C Tasks': r.pnCTasksCompleted ? 'Completed' : 'Incomplete',
      'IT Tasks': r.itTasksCompleted ? 'Completed' : 'Incomplete'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = { Sheets: { 'Offboarding': ws }, SheetNames: ['Offboarding'] };
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Offboarding_Records_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  getEmployeeDisplay(record: OffboardingRecord): string {
    return record.companyName ? `${record.employeeName} | ${record.companyName}` : record.employeeName;
  }

  viewRecord(record: OffboardingRecord): void {
    this.offboardingService.getRecordById(record.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (full) => {
          this.viewingRecord = full;
          this.isEditingView = false;
          this.displayViewDialog = true;
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load record' })
      });
  }

  startEditView(): void {
    if (!this.viewingRecord) return;
    this.editResignDate = new Date(this.viewingRecord.resignDate);
    this.editReason = this.viewingRecord.reason;
    this.isEditingView = true;
  }

  saveEditView(): void {
    if (!this.viewingRecord) return;
    // Update via the existing record — currently the API doesn't have a PUT for record fields,
    // so we close and reload. If a PUT endpoint is added later, wire it here.
    this.displayViewDialog = false;
    this.loadRecords();
  }

  editFromView(): void {
    if (!this.viewingRecord) return;
    const record = this.viewingRecord;
    this.formEditMode = true;
    this.formEditRecordId = record.id;
    this.resignDateStr = record.resignDate ? record.resignDate.substring(0, 10) : new Date().toISOString().substring(0, 10);
    this.selectedReason = record.reason || 'Personal';
    this.formAttachmentUrl = record.attachmentUrl || null;
    this.formAttachmentName = record.attachmentUrl ? record.attachmentUrl.split('/').pop() || 'Attachment' : null;
    this.formSubmitted = false;
    this.displayViewDialog = false;
    this.viewingRecord = null;

    // Load employees if not already loaded, then open form
    if (!this.employees.length) {
      this.employeeService.getAllEmployees()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (emps) => {
            this.employees = emps.filter(e => e.isActive);
            this.selectedEmployee = this.employees.find(e => e.id === record.employeeId) ?? null;
            this.displayFormDialog = true;
          }
        });
    } else {
      this.selectedEmployee = this.employees.find(e => e.id === record.employeeId) ?? null;
      this.displayFormDialog = true;
    }
  }

  closeViewDialog(): void {
    this.displayViewDialog = false;
    this.viewingRecord = null;
    this.isEditingView = false;
  }

  formatDate(dateStr?: string): string {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return dateStr; }
  }

  cancelRecord(record: OffboardingRecord): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to cancel the offboarding for "${record.employeeName}"?`,
      header: 'Confirm Cancel',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.offboardingService.cancelRecord(record.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Record cancelled' });
              // Optimistic update
              const r = this.records.find(x => x.id === record.id);
              if (r) r.status = 'Cancelled';
              this.applyFilters();
            },
            error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to cancel record' }); }
          });
      }
    });
  }

  activateRecord(record: OffboardingRecord): void {
    this.confirmationService.confirm({
      message: `Reactivate offboarding for "${record.employeeName}"?`,
      header: 'Confirm Activate',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.offboardingService.activateRecord(record.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Record activated' });
              // Optimistic update
              const r = this.records.find(x => x.id === record.id);
              if (r) r.status = 'InProgress';
              this.applyFilters();
            },
            error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to activate record' }); }
          });
      }
    });
  }

  /** Admin-only: permanent delete is destructive, so it is not offered to P&C. */
  get isAdmin(): boolean {
    return this.authService.hasAdminAccess();
  }

  /**
   * Deletable only once the offboarding is no longer active — Completed or Cancelled.
   * An InProgress record must be cancelled first (the API enforces this too).
   */
  canDeleteRecord(record: OffboardingRecord): boolean {
    return this.isAdmin && (record.status === 'Completed' || record.status === 'Cancelled');
  }

  deleteRecord(record: OffboardingRecord): void {
    this.confirmationService.confirm({
      message: `Permanently delete the ${record.status.toLowerCase()} offboarding record for "${record.employeeName}"? `
        + 'This also removes its P&C/IT tasks and exit questionnaire. This action cannot be undone.',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.offboardingService.deleteRecord(record.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Record deleted',
                detail: `Offboarding record for ${record.employeeName} was permanently deleted.`
              });
              // Drop locally by identity, then refresh from the server for accurate state.
              this.records = this.records.filter(r => r.id !== record.id);
              this.applyFilters();
              this.loadRecords();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Delete failed',
                detail: error?.error?.message || 'Failed to delete the offboarding record.'
              });
            }
          });
      }
    });
  }

  // ===== Task Dialog =====

  openTaskDialog(record: OffboardingRecord, department: string): void {
    this.offboardingService.getRecordById(record.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (full) => {
          this.selectedRecord = full;
          this.dialogDepartment = department;
          this.dialogTasks = full.tasks.filter(t => t.department === department).sort((a, b) => a.sortOrder - b.sortOrder);
          this.displayTaskDialog = true;
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load record' })
      });
  }

  closeTaskDialog(): void {
    this.displayTaskDialog = false;
    this.selectedRecord = null;
  }

  saveDeptTasks(): void {
    if (!this.selectedRecord) return;
    this.savingTasks = true;
    const recordId = this.selectedRecord.id;
    const updates = this.dialogTasks.map(t => ({ id: t.id, isCompleted: t.isCompleted }));
    this.offboardingService.updateTasks(recordId, updates)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Tasks updated' });
          this.savingTasks = false;
          this.closeTaskDialog();
          // Background refresh to get accurate status from server
          this.loadRecords();
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save' });
          this.savingTasks = false;
        }
      });
  }

  isDeptAllDone(dept: string): boolean {
    const tasks = this.getTasksByDept(dept);
    return tasks.length > 0 && tasks.every(t => t.isCompleted);
  }

  toggleDept(dept: string, checked: boolean): void {
    this.getTasksByDept(dept).forEach(t => t.isCompleted = checked);
  }

  getTasksByDept(dept: string): OffboardingTask[] {
    return this.dialogTasks.filter(t => t.department === dept).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  getDeptLabel(dept: string): string {
    return dept === 'PnC' ? 'P&C' : dept;
  }

  // ===== Q&A Dialog =====

  openQADialog(record: OffboardingRecord): void {
    this.offboardingService.getQuestionnaire(record.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (qa) => {
          // Merge fresh questionnaire data (emailSentDate, isCompleted) into the record so the dialog
          // reflects the latest state — the email is sent asynchronously after record creation, so
          // the list-level record may still show stale "Email Not Sent" until refreshed.
          this.selectedRecord = {
            ...record,
            exitQuestionnaire: {
              ...(record.exitQuestionnaire ?? {} as any),
              ...qa
            }
          };
          this.qaAnswers = qa.answers.map(a => ({ id: a.id, question: a.question, answer: a.answer || '' }));
          this.displayQADialog = true;
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load questionnaire' })
      });
  }

  closeQADialog(): void {
    this.displayQADialog = false;
  }

  saveQA(): void {
    if (!this.selectedRecord) return;
    this.savingQA = true;
    this.offboardingService.updateQuestionnaire(this.selectedRecord.id, this.qaAnswers as any)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Questionnaire updated' });
          this.savingQA = false;
          this.closeQADialog();
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save' });
          this.savingQA = false;
        }
      });
  }

  getQuestionnaireUrl(): string {
    const token = this.selectedRecord?.exitQuestionnaire?.accessToken;
    if (!token) return '';
    const origin = window.location.origin;
    return `${origin}/questionnaire/${token}`;
  }

  copyQuestionnaireLink(): void {
    const url = this.getQuestionnaireUrl();
    if (url) navigator.clipboard.writeText(url);
  }

  sendQuestionnaireEmail(): void {
    // Opens default mail client with pre-filled questionnaire link
    const url = this.getQuestionnaireUrl();
    const name = this.selectedRecord?.employeeName ?? '';
    const subject = encodeURIComponent('Exit Questionnaire - BISTEC Global');
    const body = encodeURIComponent(
      `Hi ${name},\n\nAs part of your offboarding process, we kindly request you to complete the exit questionnaire using the link below:\n\n${url}\n\nYour responses are confidential and will help us improve.\n\nThank you,\nP&C Team`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  }

  isTaskCompleted(record: OffboardingRecord, department: string): boolean {
    switch (department) {
      case 'PnC': return record.pnCTasksCompleted;
      case 'IT': return record.itTasksCompleted;
      default: return false;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'InProgress': return 'status-in-progress';
      case 'Completed': return 'status-completed';
      case 'Cancelled': return 'status-cancelled';
      default: return '';
    }
  }

  private formatDateForApi(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
